import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { AxiosError } from 'axios'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import { FormGroup, Input, Label } from '~/components/unform'
import Form from '~/components/unform/Form'
import Select from '~/components/unform/ReactSelect'
import { externalCampaignMetricsKey } from '~/modules/retailMedia/services/metrics'

import { channelOptions } from '../constants'
import FileUploadArea from './elements/FileUploadArea'
import ErrorView from './error'
import { useExternalCampaignMetrics } from './hooks/useExternalCampaignMetrics'
import SuccessView from './success'

import * as S from './styles'

type FormData = {
  channel: string
  placement: string
  file?: File
}

interface ExternalCampaignMetricsFormProps {
  onSuccess?: () => void
}

const ExternalCampaignMetricsPage = ({
  onSuccess
}: ExternalCampaignMetricsFormProps) => {
  const formRef = useRef<FormHandles>(null)
  const queryClient = useQueryClient()

  const [hasChannel, setHasChannel] = useState(true)
  const [hasPlacement, setHasPlacement] = useState(false)
  const [hasFile, setHasFile] = useState(false)
  const [showError, setShowError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [formData, setFormData] = useState<{
    channel?: string
    placement?: string
    fileName?: string
  } | null>(null)
  const [savedFormValues, setSavedFormValues] = useState<{
    channel?: string
    placement?: string
  } | null>(null)
  const [fileInputKey, setFileInputKey] = useState(0)

  const handleSuccessCallback = useCallback(() => {
    // Invalidate and refetch the table query
    queryClient.invalidateQueries({
      queryKey: [`${externalCampaignMetricsKey}-list`]
    })
    // Clear only the file field, keep channel and placement
    formRef.current?.setFieldValue('file', null)
    setHasFile(false)
    // Show success view
    setShowSuccess(true)
  }, [queryClient])

  const handleCloseSuccess = useCallback(() => {
    setShowSuccess(false)
    // Restore saved form values if they exist
    if (savedFormValues) {
      formRef.current?.setData({
        channel: savedFormValues.channel,
        placement: savedFormValues.placement,
        file: null
      })
      setHasChannel(!!savedFormValues.channel)
      setHasPlacement(!!savedFormValues.placement)
      setHasFile(false)
    } else {
      // If no saved values, just clear the file
      formRef.current?.setFieldValue('file', null)
      setHasFile(false)
    }
    // Reset file input by changing key to force remount
    setFileInputKey(prev => prev + 1)
    // Close the modal
    onSuccess?.()
  }, [onSuccess, savedFormValues])

  const {
    mutate: handleImport,
    isPending,
    error,
    reset: resetMutation
  } = useExternalCampaignMetrics({
    onSuccess: handleSuccessCallback
  })

  const defaultChannel = channelOptions[0]

  const isFormValid = useMemo(
    () => hasChannel && hasPlacement && hasFile,
    [hasChannel, hasPlacement, hasFile]
  )

  const handleChannelChange = useCallback(
    (selectedOption: { value: string; label: string } | null) => {
      const isValid = !!selectedOption?.value
      setHasChannel(isValid)
      if (isValid) {
        formRef.current?.setFieldError('channel', null)
      } else {
        formRef.current?.setFieldError(
          'channel',
          t('rm:externalCampaignMetrics.form.channel.error.required')
        )
      }
    },
    []
  )

  const handlePlacementBlur = useCallback(
    (blurData: { value: string; clearError: () => void }) => {
      const { value, clearError } = blurData
      if (!value || value.trim() === '') {
        formRef.current?.setFieldError(
          'placement',
          t('rm:externalCampaignMetrics.form.placement.error.required')
        )
      } else {
        clearError()
      }
    },
    []
  )

  const handlePlacementChange = useCallback(({ value }: { value: string }) => {
    const isValid = value && value.trim() !== ''
    setHasPlacement(!!isValid)
    if (isValid) {
      formRef.current?.setFieldError('placement', null)
    }
  }, [])

  const handleSubmit = (data: FormData) => {
    const file = data?.file

    if (!data.channel || data.channel.trim() === '') {
      formRef.current?.setFieldError(
        'channel',
        t('rm:externalCampaignMetrics.form.channel.error.required')
      )
    }

    if (!data.placement || data.placement.trim() === '') {
      formRef.current?.setFieldError(
        'placement',
        t('rm:externalCampaignMetrics.form.placement.error.required')
      )
    }

    if (!file) {
      formRef.current?.setFieldError(
        'file',
        t('rm:externalCampaignMetrics.form.file.error.required')
      )
    }

    if (!data.channel || !data.placement || !file) {
      return
    }

    const channelLabel =
      channelOptions.find(opt => opt.value === data.channel)?.label ||
      data.channel
    setFormData({
      channel: channelLabel,
      placement: data.placement,
      fileName: file.name
    })

    // Save form values to maintain them when coming back
    setSavedFormValues({
      channel: data.channel,
      placement: data.placement
    })

    setShowError(false)
    setShowSuccess(false)
    handleImport({
      file,
      channel: data.channel,
      placement_name: data.placement
    })
  }

  // Monitor error state and show error view when mutation fails
  const mutationError = error as AxiosError<unknown> | null
  const shouldShowError =
    showError || (mutationError && !isPending && !showSuccess)

  const handleBackToForm = useCallback(() => {
    setShowError(false)
    resetMutation() // Reset mutation error state
    // Restore saved form values if they exist, otherwise reset
    if (savedFormValues) {
      formRef.current?.setData({
        channel: savedFormValues.channel,
        placement: savedFormValues.placement,
        file: null
      })
      setHasChannel(!!savedFormValues.channel)
      setHasPlacement(!!savedFormValues.placement)
    } else {
      formRef.current?.reset()
      setHasChannel(true)
      setHasPlacement(false)
    }
    setHasFile(false)
    setFormData(null) // Reset form data
    // Reset file input by changing key to force remount
    setFileInputKey(prev => prev + 1)
  }, [resetMutation, savedFormValues])

  // Auto-show error when mutation completes with error
  useEffect(() => {
    if (mutationError && !isPending && !showError && !showSuccess) {
      setShowError(true)
    }
  }, [mutationError, isPending, showError, showSuccess])

  const showResult = showSuccess || shouldShowError

  return (
    <S.SliderContainer>
      <S.SliderWrapper $translate={showResult ? '-50%' : '0%'}>
        <S.Slide>
          <Form
            id="external-campaign-metrics-form"
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            initialData={{ channel: 'google_pla' }}
          >
            <FormGroup className="mb-4">
              <Label
                htmlFor="channel"
                text={t('rm:externalCampaignMetrics.form.channel.label')}
                isRequired
              />
              <Select
                name="channel"
                placeholder={t(
                  'rm:externalCampaignMetrics.form.channel.placeholder'
                )}
                options={channelOptions}
                defaultValue={defaultChannel}
                onChange={handleChannelChange}
              />
            </FormGroup>

            <FormGroup className="mb-4">
              <Label
                htmlFor="placement"
                text={t('rm:externalCampaignMetrics.form.placement.label')}
                isRequired
              />
              <Input
                id="placement"
                name="placement"
                placeholder={t(
                  'rm:externalCampaignMetrics.form.placement.placeholder'
                )}
                // @ts-expect-error: Input onBlur receives custom object, not FocusEvent
                onBlur={handlePlacementBlur}
                onChange={handlePlacementChange}
              />
            </FormGroup>

            <FileUploadArea
              key={fileInputKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const hasFileSelected = !!e.target.files?.length
                setHasFile(hasFileSelected)
                if (hasFileSelected) {
                  formRef.current?.setFieldError('file', null)
                }
              }}
            />
          </Form>
          <FormGroup className="actionButtons">
            <Button
              type="submit"
              form="external-campaign-metrics-form"
              text={t('rm:externalCampaignMetrics.form.button.import')}
              template="primary"
              loading={isPending}
              disabled={!isFormValid || isPending}
            />
          </FormGroup>
        </S.Slide>

        <S.Slide>
          {showSuccess ? (
            <SuccessView onClose={handleCloseSuccess} />
          ) : shouldShowError ? (
            <ErrorView
              error={mutationError}
              onBack={handleBackToForm}
              formData={formData || undefined}
            />
          ) : null}
        </S.Slide>
      </S.SliderWrapper>
    </S.SliderContainer>
  )
}

export default ExternalCampaignMetricsPage
